/**
 * UAT-MEDICAL-ACCESS-REQUEST-01 — Patient decision surface.
 *
 * A Provider request authorizes NOTHING. Only the Patient's explicit Approve
 * creates the canonical person-level grant (written server-side by the
 * existing grant authority, which re-validates eligibility first).
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ShieldQuestion, Check, X, Loader2 } from 'lucide-react';
import api from '../utils/api';
import { toast } from '../hooks/use-toast';

export const PatientAccessRequestInbox = ({ onDecided }) => {
  const { t } = useTranslation();
  const [requests, setRequests] = useState([]);
  const [busy, setBusy] = useState(null);
  const [loading, setLoading] = useState(true);

  const roleLabel = (role) => {
    const key = `patient.medicalFiles.recipientRoles.${role}`;
    const label = t(key);
    return label && label !== key ? label : role;
  };

  const load = useCallback(async () => {
    try {
      const r = await api.get('/patient/medical-access-requests', { params: { status: 'pending' } });
      setRequests(r?.data?.requests || []);
    } catch {
      setRequests([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const decide = async (req, decision) => {
    setBusy(req.request_id);
    try {
      await api.post(`/patient/medical-access-requests/${req.request_id}/decision`, { decision });
      toast({
        title: decision === 'approved'
          ? t('patient.medicalFiles.accessRequests.approvedTitle')
          : t('patient.medicalFiles.accessRequests.rejectedTitle'),
        description: decision === 'approved'
          ? t('patient.medicalFiles.accessRequests.approvedBody', { name: req.target_name || req.target_user_id })
          : t('patient.medicalFiles.accessRequests.rejectedBody'),
      });
      await load();
      onDecided?.();
    } catch (err) {
      toast({
        title: t('patient.common.error'),
        description: err?.response?.data?.message || t('patient.medicalFiles.accessRequests.decisionFailed'),
        variant: 'destructive',
      });
    } finally {
      setBusy(null);
    }
  };

  if (loading) return <Loader2 className="h-4 w-4 animate-spin text-slate-300 mb-4" />;
  if (!requests.length) return null;

  return (
    <div className="mb-6 space-y-2" data-testid="patient-access-requests">
      <p className="text-[11px] font-semibold text-amber-700 uppercase tracking-wide flex items-center gap-1.5">
        <ShieldQuestion className="h-3.5 w-3.5" />
        {t('patient.medicalFiles.accessRequests.heading')}
      </p>
      {requests.map((r) => (
        <div
          key={r.request_id}
          className="bg-amber-50 border border-amber-200 rounded-lg p-3"
          data-testid={`access-request-${r.request_id}`}
        >
          <p className="text-xs text-slate-800 leading-relaxed" data-testid={`access-request-text-${r.request_id}`}>
            {t('patient.medicalFiles.accessRequests.sentence', {
              clinic: r.clinic_name || r.clinic_id,
              person: r.target_name || r.target_user_id,
              role: roleLabel(r.target_role),
              file: r.file_name,
              purpose: t(`provider.messaging.accessRequest.purpose.${r.purpose}`),
            })}
          </p>
          {r.requester_name && (
            <p className="text-[11px] text-slate-500 mt-1">
              {t('patient.medicalFiles.accessRequests.requestedBy', {
                name: r.requester_name, role: roleLabel(r.requester_role),
              })}
            </p>
          )}
          <div className="flex items-center gap-2 mt-2">
            <button
              onClick={() => decide(r, 'approved')}
              disabled={busy === r.request_id}
              className="text-[11px] font-medium px-2.5 py-1 rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-1"
              data-testid={`access-request-approve-${r.request_id}`}
            >
              <Check className="h-3 w-3" /> {t('patient.medicalFiles.accessRequests.approve')}
            </button>
            <button
              onClick={() => decide(r, 'rejected')}
              disabled={busy === r.request_id}
              className="text-[11px] font-medium px-2.5 py-1 rounded border border-slate-300 text-slate-700 hover:bg-white disabled:opacity-50 flex items-center gap-1"
              data-testid={`access-request-reject-${r.request_id}`}
            >
              <X className="h-3 w-3" /> {t('patient.medicalFiles.accessRequests.reject')}
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

export default PatientAccessRequestInbox;
